import { useContext, useEffect, useState } from "react";
import { ModalContext } from "../../../utils/modal/ModalProvider";
import { DialogEnum, showDialog } from "../../../utils/dialog/Dialog";
import axios from "../../../utils/api/axios";
import styles from '../../../utils/modal/DetailModal.module.css';
import modalStyles from "./ServiceUserDetailModal.module.css";
import { InnocodeButton, InnocodeDropdown, InnocodeBadge } from "@intsoft/innocode-core";
import { IAM_SERVER_TENANT_USER, IAM_SERVER_SERVICE_AUTH, IAM_SERVER_SERVICE_USER } from "../../../utils/Config";
import { ServiceUserSelectModal } from "./ServiceUserSelectModal";
import DetailModal from "../../../utils/modal/DetailModal";

/**
 * 서비스 관리자 등록 모달
 * @param update
 * @param service
 * @returns {JSX.Element}
 * @constructor
 */
export const ServiceUserAdminRegisterModal = ({ update, service }) => {
    const { closeModal, openModal } = useContext(ModalContext);

    // 데이터 상태
    const [serviceAuthList, setServiceAuthList] = useState([]); // 서비스 권한 목록

    // 선택 상태
    const [selectedUsers, setSelectedUsers] = useState([]); // 선택된 유저 목록 (다중)
    const [selectedAuths, setSelectedAuths] = useState([]); // 선택된 권한 ID 목록

    const [isUserSelectOpen, setIsUserSelectOpen] = useState(false);

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (service) {
            fetchData();
        }
    }, [service]);

    const fetchData = async () => {
        setLoading(true);
        try {
            // 서비스 권한 목록 조회
            const authRes = await axios.post(IAM_SERVER_SERVICE_AUTH + '/all', {
                sortField: ['createdDate'],
                sortOrder: ['desc'],
                operator: "AND",
                conditions: [{ field: "serviceId", operator: "EQUALS", value: service.serviceId }]
            });

            // 권한 필터링: 'SERVICE' 타입만 표시 + 'TENANT_USER' 포함
            const allAuths = authRes.data.data.data || [];
            const authsWithServiceAdmin = allAuths.some(auth => auth.authorityEngName === 'SYS_ADMIN')
                ? allAuths
                : [...allAuths, {
                    authorityEngName: 'SYS_ADMIN',
                    authorityName: '서비스 관리자',
                    authorityType: 'SERVICE',
                    serviceId: service.serviceId
                }];
            const filteredAuths = authsWithServiceAdmin.filter(auth =>
                auth.authorityType === 'SERVICE'
                || auth.authorityEngName === 'TENANT_USER'
                || auth.authorityEngName === 'SYS_ADMIN'
            );

            setServiceAuthList(filteredAuths);

            // 초기 선택 권한 설정 (요구사항: "테넌트 유저 고정")
            setSelectedAuths(['TENANT_USER']);

        } catch (error) {
            console.error("데이터 조회 실패:", error);
            showDialog(DialogEnum.DEFAULT, "오류", "데이터를 불러오는데 실패했습니다.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        console.log("selectedUsers changed:", selectedUsers);
    }, [selectedUsers]);


    // 사용자 선택 모달 열기
    const handleOpenUserSelectModal = () => {
        const modalContent = <ServiceUserSelectModal service={service}
            onConfirm={(users) => {
                console.log("onConfirm", users);
                setSelectedUsers(users);
            }}
            onClose={() => closeModal()}
        />
        const modalOption = { modalTitle: '사용자 선택' }
        openModal(<DetailModal content={modalContent} option={modalOption} />)
    };

    // 선택된 사용자 제거
    const handleRemoveUser = (tenantUserId) => {
        setSelectedUsers(prev => prev.filter(u => u.tenantUserId !== tenantUserId));
    };

    // 권한 추가 핸들러
    const handleAddAuth = (authEngName) => {
        if (!selectedAuths.includes(authEngName)) {
            setSelectedAuths(prev => [...prev, authEngName]);
        }
    };

    // 권한 제거 핸들러
    const handleRemoveAuth = (authEngName) => {
        // TENANT_USER는 고정이므로 제거 불가 (요구사항)
        if (authEngName === 'TENANT_USER') {
            return;
        }
        setSelectedAuths(prev => prev.filter(a => a !== authEngName));
    };

    // 등록 핸들러 (다중 처리)
    const handleRegister = async () => {
        if (selectedUsers.length === 0) {
            showDialog(DialogEnum.DEFAULT, "알림", "사용자를 선택해주세요.");
            return;
        }

        // 'TENANT_USER' 권한 기본 포함
        const authorityIds = [...selectedAuths];
        if (!authorityIds.includes('TENANT_USER')) {
            authorityIds.push('TENANT_USER');
        }

        const promises = selectedUsers.map(user => {
            const payload = {
                profileId: user.tenantUserId,
                serviceId: service.serviceId,
                authorityIds: authorityIds,
                groupIds: [],
                membershipType: 'TENANT',
                status: true
            };
            return axios.post(IAM_SERVER_SERVICE_USER + "/create", payload);
        });

        try {
            await Promise.all(promises);
            showDialog(DialogEnum.SUCCESS, "성공", `${selectedUsers.length}명의 관리자가 등록되었습니다.`, () => {
                update();
                closeModal();
            });
        } catch (err) {
            console.error("등록 실패:", err);
            showDialog(DialogEnum.DEFAULT, "실패", err.response?.data?.message || "등록 중 오류가 발생했습니다.");
        }
    };

    // 드롭다운 옵션 (권한) - 이미 선택된 것 제외
    const authOptions = serviceAuthList
        .filter(auth => !selectedAuths.includes(auth.authorityEngName))
        .map(auth => ({
            value: auth.authorityEngName,
            label: auth.authorityName
        }));

    return (
        <div style={{ padding: '0 4px' }}>
            <ul className={styles.layer__listWrap}>
                {/* 1. 사용자 선택 */}
                <li className={styles.layer__list}>
                    <p className={styles.layer__listTit}>사용자 선택</p>
                    <InnocodeButton
                        size="md"
                        designType="assistive"
                        theme="line"
                        fullWidth={true}
                        onClick={handleOpenUserSelectModal}
                        icon={
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24">
                                <path stroke="currentColor" strokeLinecap="round" strokeWidth="2" d="M16 19h4a1 1 0 0 0 1-1v-1a3 3 0 0 0-3-3h-2m-2.236-4a3 3 0 1 0 0-4M3 18v-1a3 3 0 0 1 3-3h4a3 3 0 0 1 3 3v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1Zm8-10a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                            </svg>
                        }
                    >
                        테넌트 유저 선택 {selectedUsers.length > 0 && `(${selectedUsers.length}명)`}
                    </InnocodeButton>
                </li>

                {/* 2. 선택된 사용자 카드 목록 */}
                {selectedUsers.length > 0 && (
                    <li className={styles.layer__list}>
                        <p className={styles.layer__listTit}>선택된 사용자 ({selectedUsers.length}명)</p>
                        <div style={{
                            maxHeight: '240px',
                            overflowY: 'auto',
                            border: '1px solid #f0f0f0',
                            borderRadius: '8px',
                            padding: '8px',
                            backgroundColor: '#fafafa'
                        }}>
                            {selectedUsers.map(user => (
                                <div key={user.tenantUserId} style={{
                                    padding: '12px',
                                    marginBottom: '8px',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    backgroundColor: '#fff',
                                    transition: 'box-shadow 0.2s',
                                }}>
                                    <img
                                        src={user.profileImgUrl || "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                                        alt="프로필 이미지"
                                        style={{
                                            width: '40px',
                                            height: '40px',
                                            borderRadius: '50%',
                                            objectFit: 'cover',
                                            border: '1px solid #ddd'
                                        }}
                                    />
                                    <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '12px', flex: 1, overflow: 'hidden' }}>
                                        <span style={{ fontWeight: '600', fontSize: '14px', color: '#333' }}>{user.profileName}</span>
                                        <span style={{ fontSize: '12px', color: '#666', marginTop: '2px', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                                            {user.unifiedProfileId}
                                        </span>
                                    </div>
                                    <div style={{ marginLeft: 'auto', paddingLeft: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <InnocodeBadge theme="line" type="assistive" size="sm">
                                            {user.tenantType}
                                        </InnocodeBadge>
                                        <button
                                            onClick={() => handleRemoveUser(user.tenantUserId)}
                                            style={{
                                                border: 'none',
                                                background: 'none',
                                                cursor: 'pointer',
                                                color: '#999',
                                                fontSize: '18px',
                                                padding: '4px',
                                                lineHeight: 1
                                            }}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </li>
                )}


                {/* 3. 권한 설정 */}
                <li className={styles.layer__list}>
                    <p className={styles.layer__listTit}>권한 정보</p>

                    <div className={modalStyles.authTagContainer}>
                        {/* 선택된 권한 태그 목록 */}
                        <div className={modalStyles.tagList}>
                            {selectedAuths.map(authEngName => {
                                // serviceAuthList에는 'USER'가 제외되어 있고, 'TENANT_USER'는 있을 수도 없을 수도 있음(fetch 결과에 따라).
                                // 따라서 이름 표시를 위해 전체 조회된 리스트나 별도 매핑이 필요할 수 있음.
                                // 여기서는 serviceAuthList에서 찾고 없으면 authEngName 그대로 표시.
                                const authInfo = serviceAuthList.find(a => a.authorityEngName === authEngName)
                                    || { authorityName: authEngName };
                                const isFixed = authEngName === 'TENANT_USER';

                                return (
                                    <InnocodeBadge
                                        key={authEngName}
                                        theme={"line"}
                                        type={"assistive"}
                                        size={"md"}
                                    >
                                        {authInfo.authorityName}
                                        {!isFixed && (
                                            <button
                                                onClick={() => handleRemoveAuth(authEngName)}
                                                style={{ marginLeft: '4px', cursor: 'pointer', border: 'none', background: 'none' }}
                                            >
                                                ×
                                            </button>
                                        )}
                                    </InnocodeBadge>
                                );
                            })}
                        </div>

                        {/* 권한 추가 드롭다운 */}
                        <div className={modalStyles.selectList}>
                            <InnocodeDropdown
                                placeholder="권한 추가"
                                value=""
                                options={authOptions}
                                onChange={(val) => handleAddAuth(val)}
                                disabled={authOptions.length === 0}
                            />
                        </div>
                    </div>
                </li>
            </ul>

            {/* 하단 버튼 */}
            <div className={styles.layer__saveBtnBox}>
                <InnocodeButton
                    size="md"
                    designType="primary"
                    fullWidth={true}
                    className={styles.layer__saveBtn}
                    onClick={handleRegister}
                >
                    등록
                </InnocodeButton>
            </div>
        </div>
    )
}
